import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API, toast } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Wallet,
  Search,
  RefreshCw,
  Star,
  ShieldCheck,
  PlusCircle,
  MinusCircle,
  SlidersHorizontal,
  FileText,
  Download,
} from 'lucide-react';

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
const DEFAULT_HISTORY_PAGINATION = { page: 1, page_size: 50, total: 0, has_next: false };
const DEFAULT_OPERATION_FORM = { amount: '', reason: '', reference_id: '' };
const HISTORY_TYPE_OPTIONS = [
  { value: 'all', label: 'Tous' },
  { value: 'credit', label: 'Credits' },
  { value: 'debit', label: 'Debits' },
  { value: 'adjustment', label: 'Ajustements' },
];

const formatMoney = (value, currency = 'FCFA') => {
  const amount = Number(value || 0);
  return `${amount.toLocaleString('fr-FR')} ${currency}`;
};

const formatDateTime = (value) => {
  if (!value) {
    return '-';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString('fr-FR');
};

const normalizeWalletSellerPayload = (seller, walletOverride) => {
  if (!seller) {
    return null;
  }
  const wallet = walletOverride || {};
  return {
    ...seller,
    wallet_balance: wallet.balance ?? seller.wallet_balance ?? 0,
    wallet_currency: wallet.currency ?? seller.wallet_currency ?? 'FCFA',
    wallet_status: wallet.status ?? seller.wallet_status ?? 'active',
  };
};

const resolveWalletActionSuccessMessage = (payload, fallbackMessage) => {
  const candidate = typeof payload?.message === 'string' ? payload.message.trim() : '';
  return candidate || fallbackMessage;
};

const resolveWalletErrorMessage = (detail, actionType) => {
  switch (detail) {
    case 'WALLET_REASON_REQUIRED':
      return 'La justification est obligatoire.';
    case 'INVALID_WALLET_AMOUNT':
      return actionType === 'adjustment'
        ? 'Le solde cible doit etre un montant valide superieur ou egal a zero.'
        : 'Le montant doit etre superieur a zero.';
    case 'INSUFFICIENT_BALANCE':
      return 'Le debit demande depasse le solde actuel du portefeuille.';
    case 'ADJUSTMENT_NO_CHANGE':
      return 'Le solde cible est identique au solde actuel.';
    case 'VENDOR_NOT_FOUND':
      return 'Vendeur introuvable.';
    case 'UNSUPPORTED_WALLET_USER':
      return 'Seuls les vendeurs disposent de ce portefeuille administrable.';
    default:
      return detail || 'Operation wallet impossible pour le moment.';
  }
};

const resolveOperationBadgeClasses = (type) => {
  if (type === 'credit') {
    return 'bg-green-100 text-green-700';
  }
  if (type === 'debit') {
    return 'bg-red-100 text-red-700';
  }
  return 'bg-amber-100 text-amber-700';
};

const resolveTransactionDeltaLabel = (transaction, currency = 'FCFA') => {
  const operationType = String(transaction?.operation_type || '').toLowerCase();
  const metadata = transaction?.metadata && typeof transaction.metadata === 'object' ? transaction.metadata : {};
  let amount = Number(transaction?.amount || 0);

  if (operationType === 'adjustment' && Number.isFinite(Number(metadata.adjustment_delta))) {
    amount = Number(metadata.adjustment_delta);
  } else if (operationType === 'debit') {
    amount = -Math.abs(amount);
  } else if (operationType === 'credit') {
    amount = Math.abs(amount);
  }

  const prefix = amount > 0 ? '+' : amount < 0 ? '-' : '';
  return `${prefix}${formatMoney(Math.abs(amount), currency)}`;
};

const WalletManagement = () => {
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeLetter, setActiveLetter] = useState('');
  const [loadingSellers, setLoadingSellers] = useState(false);
  const [sellerResults, setSellerResults] = useState([]);

  const [selectedSellerId, setSelectedSellerId] = useState(null);
  const [selectedSeller, setSelectedSeller] = useState(null);

  const [transactions, setTransactions] = useState([]);
  const [historyPagination, setHistoryPagination] = useState(DEFAULT_HISTORY_PAGINATION);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyType, setHistoryType] = useState('all');
  const [historyDateFrom, setHistoryDateFrom] = useState('');
  const [historyDateTo, setHistoryDateTo] = useState('');
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [exportingFormat, setExportingFormat] = useState(null);

  const [creditForm, setCreditForm] = useState(DEFAULT_OPERATION_FORM);
  const [debitForm, setDebitForm] = useState(DEFAULT_OPERATION_FORM);
  const [adjustmentForm, setAdjustmentForm] = useState(DEFAULT_OPERATION_FORM);
  const [submittingAction, setSubmittingAction] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, actionType: null, payload: null, summary: '' });

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 250);
    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  const loadSellerResults = async (queryValue = debouncedSearch, letterValue = activeLetter) => {
    setLoadingSellers(true);
    try {
      const response = await axios.get(`${API}/admin/wallet/vendors/search`, {
        params: {
          query: queryValue || undefined,
          letter: letterValue || undefined,
          limit: 12,
        },
      });
      setSellerResults(response.data?.items || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de charger les vendeurs.');
      setSellerResults([]);
    } finally {
      setLoadingSellers(false);
    }
  };

  const loadTransactions = async (userId, targetPage = historyPage) => {
    if (!userId) {
      setTransactions([]);
      setHistoryPagination(DEFAULT_HISTORY_PAGINATION);
      return;
    }

    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/admin/wallet/${userId}/transactions`, {
        params: {
          page: targetPage,
          page_size: DEFAULT_HISTORY_PAGINATION.page_size,
          type: historyType !== 'all' ? historyType : undefined,
          date_from: historyDateFrom || undefined,
          date_to: historyDateTo || undefined,
        },
      });
      const payload = response.data || {};
      setTransactions(payload.items || []);
      setHistoryPagination(payload.pagination || DEFAULT_HISTORY_PAGINATION);
      setSelectedSeller((current) => normalizeWalletSellerPayload(payload.seller || current, payload.wallet));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de charger l\'historique wallet.');
      setTransactions([]);
      setHistoryPagination(DEFAULT_HISTORY_PAGINATION);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    loadSellerResults(debouncedSearch, activeLetter);
  }, [debouncedSearch, activeLetter]);

  useEffect(() => {
    setHistoryPage(1);
  }, [selectedSellerId, historyType, historyDateFrom, historyDateTo]);

  useEffect(() => {
    if (selectedSellerId) {
      loadTransactions(selectedSellerId, historyPage);
    }
  }, [selectedSellerId, historyPage, historyType, historyDateFrom, historyDateTo]);

  const walletSummary = useMemo(() => selectedSeller || null, [selectedSeller]);
  const currentWalletBalance = Number(walletSummary?.wallet_balance || 0);
  const currentWalletCurrency = walletSummary?.wallet_currency || 'FCFA';

  const refreshCurrentView = async () => {
    await Promise.all([
      loadSellerResults(debouncedSearch, activeLetter),
      selectedSellerId ? loadTransactions(selectedSellerId, historyPage) : Promise.resolve(),
    ]);
  };

  const handleHistoryExport = async (format) => {
    if (!selectedSellerId || exportingFormat) {
      return;
    }

    const normalizedFormat = format === 'xlsx' ? 'xlsx' : 'pdf';
    const endpoint = normalizedFormat === 'xlsx'
      ? `${API}/admin/wallets/export/xlsx`
      : `${API}/admin/wallets/export/pdf`;

    setExportingFormat(normalizedFormat);
    try {
      const response = await axios.get(endpoint, {
        params: {
          user_id: selectedSellerId,
          type: historyType !== 'all' ? historyType : undefined,
          date_from: historyDateFrom || undefined,
          date_to: historyDateTo || undefined,
        },
        responseType: 'blob',
      });

      const disposition = response.headers['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename=\"?([^\";]+)\"?/i);
      const fallbackName = `wallet_transactions_${selectedSellerId}.${normalizedFormat}`;
      const filename = filenameMatch?.[1] || fallbackName;

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`Export ${normalizedFormat.toUpperCase()} genere`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Impossible de generer l'export ${normalizedFormat.toUpperCase()}.`);
    } finally {
      setExportingFormat(null);
    }
  };

  const handleSelectSeller = (seller) => {
    setSelectedSellerId(seller.user_id);
    setSelectedSeller(seller);
    setSearchInput(seller.full_name || seller.email || seller.phone || '');
  };

  const updateOperationForm = (actionType, field, value) => {
    const setterMap = {
      credit: setCreditForm,
      debit: setDebitForm,
      adjustment: setAdjustmentForm,
    };
    const setter = setterMap[actionType];
    if (setter) {
      setter((current) => ({ ...current, [field]: value }));
    }
  };

  const resetOperationForm = (actionType) => {
    const setterMap = {
      credit: setCreditForm,
      debit: setDebitForm,
      adjustment: setAdjustmentForm,
    };
    const setter = setterMap[actionType];
    if (setter) {
      setter(DEFAULT_OPERATION_FORM);
    }
  };

  const executeWalletAction = async (actionType, payload) => {
    if (!selectedSellerId || submittingAction) {
      return;
    }

    const endpointMap = {
      credit: `${API}/admin/wallet/credit`,
      debit: `${API}/admin/wallet/debit`,
      adjustment: `${API}/admin/wallet/adjust`,
    };

    setSubmittingAction(actionType);
    try {
      const response = await axios.post(endpointMap[actionType], payload);
      const body = response.data || {};
      const data = body.data || {};
      setSelectedSeller((current) => normalizeWalletSellerPayload(data.seller || current, data.wallet));
      resetOperationForm(actionType);
      setHistoryPage(1);
      await Promise.all([
        loadSellerResults(debouncedSearch, activeLetter),
        loadTransactions(selectedSellerId, 1),
      ]);
      toast.success(
        resolveWalletActionSuccessMessage(
          body,
          actionType === 'credit'
            ? 'Credit applique avec succes.'
            : actionType === 'debit'
              ? 'Debit applique avec succes.'
              : 'Solde ajuste avec succes.'
        )
      );
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(resolveWalletErrorMessage(detail, actionType));
    } finally {
      setSubmittingAction(null);
      setConfirmDialog({ open: false, actionType: null, payload: null, summary: '' });
    }
  };

  const prepareWalletPayload = (actionType) => {
    const form = actionType === 'credit' ? creditForm : actionType === 'debit' ? debitForm : adjustmentForm;
    const normalizedReason = String(form.reason || '').trim();
    const numericAmount = Number(form.amount);

    if (!selectedSellerId) {
      toast.error('Selectionnez d\'abord un vendeur.');
      return null;
    }
    if (!normalizedReason) {
      toast.error('La justification est obligatoire.');
      return null;
    }

    if (actionType === 'adjustment') {
      if (!Number.isFinite(numericAmount) || numericAmount < 0) {
        toast.error('Le solde cible doit etre superieur ou egal a zero.');
        return null;
      }
      return {
        user_id: selectedSellerId,
        amount: numericAmount,
        reason: normalizedReason,
        reference_id: String(form.reference_id || '').trim() || undefined,
      };
    }

    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      toast.error('Le montant doit etre superieur a zero.');
      return null;
    }

    return {
      user_id: selectedSellerId,
      amount: numericAmount,
      reason: normalizedReason,
      reference_id: String(form.reference_id || '').trim() || undefined,
    };
  };

  const handleOperationSubmit = (actionType) => {
    const payload = prepareWalletPayload(actionType);
    if (!payload) {
      return;
    }

    const targetBalance = Number(payload.amount || 0);
    const needsConfirmation =
      actionType === 'debit' ||
      (actionType === 'adjustment' && Number.isFinite(targetBalance) && targetBalance < currentWalletBalance);

    if (needsConfirmation) {
      const summary =
        actionType === 'debit'
          ? `Vous allez debiter ${formatMoney(payload.amount, currentWalletCurrency)} du portefeuille selectionne.`
          : `Vous allez reduire le solde du portefeuille a ${formatMoney(payload.amount, currentWalletCurrency)}.`;
      setConfirmDialog({
        open: true,
        actionType,
        payload,
        summary,
      });
      return;
    }

    executeWalletAction(actionType, payload);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-cyan-600" />
            Wallet Admin V2
          </CardTitle>
          <CardDescription>
            Recherchez un vendeur, pilotez son portefeuille et gardez un historique complet de toutes les operations.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-4 xl:grid-cols-[1.6fr_1fr]">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="wallet-vendor-search">Recherche vendeur</Label>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-gray-400" />
                  <Input
                    id="wallet-vendor-search"
                    className="pl-9"
                    placeholder="Nom, email, telephone ou boutique"
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Recherche dynamique par nom, email, telephone et filtre alphabetique rapide.
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={activeLetter ? 'outline' : 'default'}
                  onClick={() => setActiveLetter('')}
                >
                  Tous
                </Button>
                {ALPHABET.map((letter) => (
                  <Button
                    key={letter}
                    type="button"
                    size="sm"
                    variant={activeLetter === letter ? 'default' : 'outline'}
                    onClick={() => setActiveLetter((current) => (current === letter ? '' : letter))}
                  >
                    {letter}
                  </Button>
                ))}
              </div>

              <div className="rounded-lg border bg-white">
                <div className="flex items-center justify-between border-b px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Resultats vendeurs</p>
                    <p className="text-xs text-gray-500">{loadingSellers ? 'Chargement en cours...' : `${sellerResults.length} resultat(s)`}</p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={refreshCurrentView} disabled={loadingSellers || loadingHistory}>
                    <RefreshCw className={`mr-2 h-4 w-4 ${(loadingSellers || loadingHistory) ? 'animate-spin' : ''}`} />
                    Actualiser
                  </Button>
                </div>

                {sellerResults.length === 0 ? (
                  <div className="px-4 py-6 text-sm text-gray-500">Aucun vendeur trouve pour cette recherche.</div>
                ) : (
                  <div className="max-h-72 overflow-y-auto divide-y">
                    {sellerResults.map((seller) => {
                      const isSelected = seller.user_id === selectedSellerId;
                      return (
                        <button
                          key={seller.user_id}
                          type="button"
                          onClick={() => handleSelectSeller(seller)}
                          className={`flex w-full items-start justify-between gap-4 px-4 py-3 text-left transition ${isSelected ? 'bg-cyan-50' : 'hover:bg-gray-50'}`}
                        >
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-gray-900">{seller.full_name || 'Vendeur sans nom'}</p>
                              <Badge className={seller.is_pro_active ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'}>
                                {seller.is_pro_active ? 'Pro' : 'Standard'}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-600">{seller.email || '-'}</p>
                            <p className="text-xs text-gray-500">{seller.phone || '-'} · {seller.organization_name || '-'}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-gray-900">{formatMoney(seller.wallet_balance, seller.wallet_currency)}</p>
                            <p className="text-xs text-gray-500">{seller.wallet_status || 'active'}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <Card className="border-dashed">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Wallet selectionne</CardTitle>
                <CardDescription>
                  {walletSummary ? 'Source de verite backend avec solde actuel et statut vendeur.' : 'Selectionnez un vendeur pour afficher son portefeuille.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {walletSummary ? (
                  <>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <p className="text-lg font-semibold text-gray-900">{walletSummary.full_name || '-'}</p>
                        {walletSummary.is_pro_active ? (
                          <Badge className="bg-amber-100 text-amber-700">
                            <Star className="mr-1 h-3 w-3" /> Pro
                          </Badge>
                        ) : (
                          <Badge variant="outline">Non Pro</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{walletSummary.email || '-'}</p>
                      <p className="text-sm text-gray-600">{walletSummary.phone || '-'}</p>
                      <p className="text-xs text-gray-500">Organisation: {walletSummary.organization_name || walletSummary.organization_id || '-'}</p>
                    </div>

                    <div className="rounded-lg bg-slate-50 p-4">
                      <p className="text-xs uppercase tracking-wide text-slate-500">Solde actuel</p>
                      <p className="mt-2 text-2xl font-bold text-slate-900">{formatMoney(walletSummary.wallet_balance, walletSummary.wallet_currency)}</p>
                      <div className="mt-3 flex items-center gap-2 text-sm">
                        <Badge className={walletSummary.wallet_status === 'active' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                          {walletSummary.wallet_status || 'active'}
                        </Badge>
                        <span className="text-slate-500">Plan: {walletSummary.subscription_plan || 'free'}</span>
                      </div>
                    </div>

                    <div className="rounded-lg border bg-white p-4 text-sm text-gray-600">
                      <div className="flex items-start gap-3">
                        <ShieldCheck className="mt-0.5 h-4 w-4 text-cyan-600" />
                        <div>
                          <p className="font-medium text-gray-900">Tracabilite financiere</p>
                          <p>Toute operation wallet admin cree une transaction, un audit log et une notification automatique.</p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="rounded-lg border border-dashed p-6 text-sm text-gray-500">
                    Choisissez un vendeur dans la liste pour debloquer les actions admin et l'historique detaille.
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 xl:grid-cols-[1.1fr_1.4fr]">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Actions wallet</CardTitle>
            <CardDescription>
              Crediter, debiter ou ajuster un solde avec justification obligatoire. Les debits sont confirmes avant envoi.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="credit" className="space-y-4">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="credit">Crediter</TabsTrigger>
                <TabsTrigger value="debit">Debiter</TabsTrigger>
                <TabsTrigger value="adjustment">Ajuster</TabsTrigger>
              </TabsList>

              <TabsContent value="credit" className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="wallet-credit-amount">Montant a crediter</Label>
                  <Input
                    id="wallet-credit-amount"
                    type="number"
                    min="1"
                    value={creditForm.amount}
                    onChange={(event) => updateOperationForm('credit', 'amount', event.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wallet-credit-reason">Justification</Label>
                  <Textarea
                    id="wallet-credit-reason"
                    rows={4}
                    value={creditForm.reason}
                    onChange={(event) => updateOperationForm('credit', 'reason', event.target.value)}
                    placeholder="Exemple: regularisation commerciale, bonus exceptionnel..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wallet-credit-reference">Reference interne (optionnel)</Label>
                  <Input
                    id="wallet-credit-reference"
                    value={creditForm.reference_id}
                    onChange={(event) => updateOperationForm('credit', 'reference_id', event.target.value)}
                  />
                </div>
                <Button
                  type="button"
                  className="w-full bg-green-600 hover:bg-green-700"
                  disabled={submittingAction === 'credit' || !selectedSellerId}
                  onClick={() => handleOperationSubmit('credit')}
                >
                  <PlusCircle className="mr-2 h-4 w-4" />
                  {submittingAction === 'credit' ? 'Traitement...' : 'Crediter le wallet'}
                </Button>
              </TabsContent>

              <TabsContent value="debit" className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="wallet-debit-amount">Montant a debiter</Label>
                  <Input
                    id="wallet-debit-amount"
                    type="number"
                    min="1"
                    value={debitForm.amount}
                    onChange={(event) => updateOperationForm('debit', 'amount', event.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wallet-debit-reason">Justification</Label>
                  <Textarea
                    id="wallet-debit-reason"
                    rows={4}
                    value={debitForm.reason}
                    onChange={(event) => updateOperationForm('debit', 'reason', event.target.value)}
                    placeholder="Exemple: correction d'encaissement, annulation exceptionnelle..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wallet-debit-reference">Reference interne (optionnel)</Label>
                  <Input
                    id="wallet-debit-reference"
                    value={debitForm.reference_id}
                    onChange={(event) => updateOperationForm('debit', 'reference_id', event.target.value)}
                  />
                </div>
                <Button
                  type="button"
                  className="w-full bg-red-600 hover:bg-red-700"
                  disabled={submittingAction === 'debit' || !selectedSellerId}
                  onClick={() => handleOperationSubmit('debit')}
                >
                  <MinusCircle className="mr-2 h-4 w-4" />
                  {submittingAction === 'debit' ? 'Traitement...' : 'Debiter le wallet'}
                </Button>
              </TabsContent>

              <TabsContent value="adjustment" className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="wallet-adjustment-amount">Solde cible</Label>
                  <Input
                    id="wallet-adjustment-amount"
                    type="number"
                    min="0"
                    value={adjustmentForm.amount}
                    onChange={(event) => updateOperationForm('adjustment', 'amount', event.target.value)}
                  />
                  <p className="text-xs text-gray-500">
                    L'ajustement definit directement le nouveau solde du portefeuille. Solde actuel: {formatMoney(currentWalletBalance, currentWalletCurrency)}.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wallet-adjustment-reason">Justification</Label>
                  <Textarea
                    id="wallet-adjustment-reason"
                    rows={4}
                    value={adjustmentForm.reason}
                    onChange={(event) => updateOperationForm('adjustment', 'reason', event.target.value)}
                    placeholder="Exemple: alignement apres audit comptable..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wallet-adjustment-reference">Reference interne (optionnel)</Label>
                  <Input
                    id="wallet-adjustment-reference"
                    value={adjustmentForm.reference_id}
                    onChange={(event) => updateOperationForm('adjustment', 'reference_id', event.target.value)}
                  />
                </div>
                <Button
                  type="button"
                  className="w-full bg-amber-600 hover:bg-amber-700"
                  disabled={submittingAction === 'adjustment' || !selectedSellerId}
                  onClick={() => handleOperationSubmit('adjustment')}
                >
                  <SlidersHorizontal className="mr-2 h-4 w-4" />
                  {submittingAction === 'adjustment' ? 'Traitement...' : 'Ajuster le solde'}
                </Button>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <CardTitle>Historique des transactions</CardTitle>
              <CardDescription>
                Consultez toutes les ecritures wallet du vendeur selectionne, avec soldes avant/apres et auteur de l'action.
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleHistoryExport('pdf')}
                disabled={!selectedSellerId || loadingHistory || exportingFormat === 'pdf' || transactions.length === 0}
              >
                <FileText className="mr-2 h-4 w-4" />
                {exportingFormat === 'pdf' ? 'Generation PDF...' : 'Exporter PDF'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleHistoryExport('xlsx')}
                disabled={!selectedSellerId || loadingHistory || exportingFormat === 'xlsx' || transactions.length === 0}
              >
                <Download className="mr-2 h-4 w-4" />
                {exportingFormat === 'xlsx' ? 'Generation XLSX...' : 'Exporter XLSX'}
              </Button>
            </div>
          </div>
        </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-4">
              <div className="space-y-2">
                <Label htmlFor="wallet-history-type">Type</Label>
                <select
                  id="wallet-history-type"
                  className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={historyType}
                  onChange={(event) => setHistoryType(event.target.value)}
                >
                  {HISTORY_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="wallet-history-from">Du</Label>
                <Input id="wallet-history-from" type="date" value={historyDateFrom} onChange={(event) => setHistoryDateFrom(event.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wallet-history-to">Au</Label>
                <Input id="wallet-history-to" type="date" value={historyDateTo} onChange={(event) => setHistoryDateTo(event.target.value)} />
              </div>
              <div className="flex items-end">
                <Button type="button" variant="outline" className="w-full" onClick={() => loadTransactions(selectedSellerId, 1)} disabled={!selectedSellerId || loadingHistory}>
                  Filtrer
                </Button>
              </div>
            </div>

            {!selectedSellerId ? (
              <div className="rounded-lg border border-dashed p-6 text-sm text-gray-500">
                Selectionnez un vendeur pour consulter l'historique wallet.
              </div>
            ) : loadingHistory ? (
              <div className="text-sm text-gray-500">Chargement de l'historique...</div>
            ) : transactions.length === 0 ? (
              <div className="rounded-lg border border-dashed p-6 text-sm text-gray-500">
                Aucune transaction ne correspond a ces filtres.
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Variation</TableHead>
                      <TableHead>Solde avant</TableHead>
                      <TableHead>Solde apres</TableHead>
                      <TableHead>Motif</TableHead>
                      <TableHead>Par</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell className="text-sm text-gray-600">{formatDateTime(transaction.created_at)}</TableCell>
                        <TableCell>
                          <Badge className={resolveOperationBadgeClasses(transaction.operation_type)}>
                            {transaction.operation_label || transaction.operation_type || 'Operation'}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">{resolveTransactionDeltaLabel(transaction, walletSummary?.wallet_currency)}</TableCell>
                        <TableCell>{formatMoney(transaction.balance_before, walletSummary?.wallet_currency)}</TableCell>
                        <TableCell>{formatMoney(transaction.balance_after, walletSummary?.wallet_currency)}</TableCell>
                        <TableCell className="max-w-xs text-sm text-gray-600">{transaction.reason || '-'}</TableCell>
                        <TableCell className="text-sm text-gray-600">{transaction.performed_by_name || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">Total transactions: {historyPagination.total || 0}</p>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setHistoryPage((current) => Math.max(1, current - 1))}
                      disabled={loadingHistory || historyPage <= 1}
                    >
                      Precedent
                    </Button>
                    <span className="text-sm text-gray-600">Page {historyPage}</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setHistoryPage((current) => current + 1)}
                      disabled={loadingHistory || !historyPagination.has_next}
                    >
                      Suivant
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog
        open={confirmDialog.open}
        onOpenChange={(open) => {
          if (!open && submittingAction) {
            return;
          }
          setConfirmDialog((current) => ({ ...current, open }));
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer l'operation wallet</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.summary || 'Cette operation modifie le solde du portefeuille vendeur.'}
              {' '}Cette action sera tracee et notifiee automatiquement.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={Boolean(submittingAction)}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              disabled={Boolean(submittingAction)}
              onClick={(event) => {
                event.preventDefault();
                if (!confirmDialog.actionType || !confirmDialog.payload) {
                  return;
                }
                executeWalletAction(confirmDialog.actionType, confirmDialog.payload);
              }}
            >
              {submittingAction ? 'Traitement...' : 'Confirmer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default WalletManagement;

